import { create } from 'zustand';

// Client-only demo state for the Training & Competency Management prototype. There
// is no backend for training, so the "active", "assigned" and "completed" facts
// live here and drive the assign dialog, document overview and My training tab.
// Only activation is persisted: it is the state that must survive navigation and
// stay synchronized between browser tabs.

export const TRAINING_ACTIVE_STORAGE_KEY_PREFIX = 'confideck.training.assigned:';

const getActivationStorageKey = (documentId: string) =>
  `${TRAINING_ACTIVE_STORAGE_KEY_PREFIX}${encodeURIComponent(documentId)}`;

const getDocumentIdFromActivationStorageKey = (key: string): string | undefined => {
  if (!key.startsWith(TRAINING_ACTIVE_STORAGE_KEY_PREFIX)) return undefined;

  try {
    return decodeURIComponent(key.slice(TRAINING_ACTIVE_STORAGE_KEY_PREFIX.length));
  } catch {
    return undefined;
  }
};

const getLocalStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined;

  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};

const readActiveDocumentIds = (): string[] => {
  const storage = getLocalStorage();
  if (!storage) return [];

  const documentIds: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key || storage.getItem(key) !== 'true') continue;

      const documentId = getDocumentIdFromActivationStorageKey(key);
      if (documentId) documentIds.push(documentId);
    }
  } catch {
    return [];
  }
  return documentIds;
};

const persistActivation = (documentId: string, active: boolean) => {
  try {
    getLocalStorage()?.setItem(getActivationStorageKey(documentId), String(active));
  } catch {
    // localStorage can be unavailable in privacy-restricted browser contexts. The
    // current tab still works through the Zustand state in that case.
  }
};

const clearPersistedActivations = () => {
  const storage = getLocalStorage();
  if (!storage) return;

  try {
    const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter((key): key is string =>
      Boolean(key?.startsWith(TRAINING_ACTIVE_STORAGE_KEY_PREFIX))
    );
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // See persistActivation: reset still clears the current tab's in-memory state.
  }
};

const updateActiveDocumentIds = (documentIds: string[], documentId: string, active: boolean): string[] => {
  const nextDocumentIds = documentIds.filter((id) => id !== documentId);
  return active ? [...nextDocumentIds, documentId] : nextDocumentIds;
};

type TTrainingAssignment = {
  count: number;
  dueDate: string;
  assignedAt: number;
};

type TTrainingCompletion = {
  completedAt: number;
  completedBy: string;
};

type TTrainingStore = {
  isAssignOpen: boolean;
  activeDocumentIds: string[];
  assignment?: TTrainingAssignment;
  completion?: TTrainingCompletion;
  deactivate: (documentId: string) => void;
  openAssign: () => void;
  closeAssign: () => void;
  assign: (documentId: string, assignment: Omit<TTrainingAssignment, 'assignedAt'>) => void;
  complete: (completion: Omit<TTrainingCompletion, 'completedAt'>) => void;
  reset: () => void;
};

const useTrainingStore = create<TTrainingStore>((set) => ({
  isAssignOpen: false,
  activeDocumentIds: readActiveDocumentIds(),
  assignment: undefined,
  completion: undefined,
  deactivate: (documentId) => {
    persistActivation(documentId, false);
    set((state) => ({
      isAssignOpen: false,
      activeDocumentIds: updateActiveDocumentIds(state.activeDocumentIds, documentId, false),
    }));
  },
  openAssign: () => set({ isAssignOpen: true }),
  closeAssign: () => set({ isAssignOpen: false }),
  assign: (documentId, assignment) => {
    persistActivation(documentId, true);
    set((state) => ({
      isAssignOpen: false,
      activeDocumentIds: updateActiveDocumentIds(state.activeDocumentIds, documentId, true),
      assignment: { ...assignment, assignedAt: Date.now() },
      completion: undefined,
    }));
  },
  complete: (completion) => set({ completion: { ...completion, completedAt: Date.now() } }),
  reset: () => {
    clearPersistedActivations();
    set({ isAssignOpen: false, activeDocumentIds: [], assignment: undefined, completion: undefined });
  },
}));

// The browser emits `storage` only in the other tabs that share this origin. The
// actions above update the initiating tab directly; this listener keeps every
// already-open tab in sync with it.
const handleStorage = (event: StorageEvent) => {
  if (event.key === null) {
    const activeDocumentIds = readActiveDocumentIds();
    useTrainingStore.setState({
      activeDocumentIds,
      ...(activeDocumentIds.length === 0 ? { isAssignOpen: false } : {}),
    });
    return;
  }

  const documentId = getDocumentIdFromActivationStorageKey(event.key);
  if (!documentId) return;

  const active = event.newValue === 'true';
  useTrainingStore.setState((state) => ({
    ...(!active ? { isAssignOpen: false } : {}),
    activeDocumentIds: updateActiveDocumentIds(state.activeDocumentIds, documentId, active),
    ...(active ? { assignment: undefined, completion: undefined } : {}),
  }));
};

if (typeof window !== 'undefined') {
  window.addEventListener('storage', handleStorage);
  import.meta.hot?.dispose(() => window.removeEventListener('storage', handleStorage));
}

export default useTrainingStore;
